import { setDefaultOptions, type Locale } from 'date-fns';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

const SUPPORTED_LANGS = ['ru', 'en', 'kk', 'ky', 'ms', 'id', 'fil', 'hi', 'zh', 'ar'] as const;

type SupportedLang = (typeof SUPPORTED_LANGS)[number];

const DATE_FNS_LOCALE_LOADERS: Record<SupportedLang, () => Promise<Locale>> = {
  en: () => import('date-fns/locale/en-US').then((m) => m.enUS),
  ru: () => import('date-fns/locale/ru').then((m) => m.ru),
  kk: () => import('date-fns/locale/kk').then((m) => m.kk),
  ky: () => import('date-fns/locale/ru').then((m) => m.ru),
  ms: () => import('date-fns/locale/ms').then((m) => m.ms),
  id: () => import('date-fns/locale/id').then((m) => m.id),
  fil: () => import('date-fns/locale/en-US').then((m) => m.enUS),
  hi: () => import('date-fns/locale/hi').then((m) => m.hi),
  zh: () => import('date-fns/locale/zh-CN').then((m) => m.zhCN),
  ar: () => import('date-fns/locale/ar').then((m) => m.ar),
};

function baseLang(lng: string): SupportedLang {
  return SUPPORTED_LANGS.find((code) => lng === code || lng.startsWith(`${code}-`)) ?? 'en';
}

function syncDocumentLanguage(lng: string) {
  const base = baseLang(lng);
  document.documentElement.lang = base;
  document.documentElement.dir = i18n.dir(base);

  void DATE_FNS_LOCALE_LOADERS[base]().then((locale) => {
    setDefaultOptions({ locale });
  });
}

void i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: {
      kk: ['ru', 'en'],
      ky: ['ru', 'en'],
      id: ['ms', 'en'],
      default: ['en'],
    },
    supportedLngs: [...SUPPORTED_LANGS],
    ns: ['common', 'validation', 'errors'],
    defaultNS: 'common',
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    detection: {
      order: ['querystring', 'cookie', 'navigator', 'htmlTag'],
      caches: ['cookie'],
      lookupQuerystring: 'lang',
      lookupCookie: 'i18next',
    },
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: true,
    },
  });

i18n.on('languageChanged', syncDocumentLanguage);
if (i18n.language) syncDocumentLanguage(i18n.language);

export { i18n, SUPPORTED_LANGS };
